import type { User } from '@supabase/supabase-js';
import { LANGUAGE_STORAGE_KEY, type Language } from '../i18n';
import { supabase } from '../supabaseClient';

export const LANGUAGE_METADATA_KEY = 'preferred_language';

const userStorageKey = (userId: string) => `${LANGUAGE_STORAGE_KEY}.${userId}`;

const isLanguage = (value: unknown): value is Language => value === 'en' || value === 'fr';

const readLocalPreference = (userId: string): Language | null => {
  try {
    const stored = window.localStorage.getItem(userStorageKey(userId));
    return isLanguage(stored) ? stored : null;
  } catch {
    return null;
  }
};

const writeLocalPreference = (userId: string, language: Language): void => {
  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
    window.localStorage.setItem(userStorageKey(userId), language);
  } catch {
    // Supabase metadata remains the durable source when local storage is unavailable.
  }
};

export const languagePreferenceService = {
  getPreference(user: User): Language | null {
    const metadataLanguage = user.user_metadata?.[LANGUAGE_METADATA_KEY];
    if (isLanguage(metadataLanguage)) {
      writeLocalPreference(user.id, metadataLanguage);
      return metadataLanguage;
    }

    return readLocalPreference(user.id);
  },

  async savePreference(userId: string, language: Language): Promise<{ synced: boolean }> {
    writeLocalPreference(userId, language);

    try {
      const { error } = await supabase.auth.updateUser({
        data: { [LANGUAGE_METADATA_KEY]: language },
      });

      if (error) {
        console.error('Failed to sync language preference to Supabase', error);
        return { synced: false };
      }
    } catch (error) {
      console.error('Failed to sync language preference to Supabase', error);
      return { synced: false };
    }

    return { synced: true };
  },
};
